import {
  AddOutlined,
  ButtonDS,
  LaunchPage,
  NotInterestedOutlined,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
  TextCapitalized,
} from "@qivia/ui";
import { t } from "i18next";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { listAllVehiclesInDbAsync, selectListAllVehiclesInDb, selectListAllVehiclesInDbStatus } from "../../vehicles/vehiclesSlice";
import { selectDepartments } from "../departmentsSlice";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { selectCompany } from "../../homeSlice";

export interface DepartmentSidePanelProps {
  canAddVehicle?: boolean;
  canDeactivate?: boolean;
}

export const DepartmentSidePanel = (props: DepartmentSidePanelProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const firstPath = location.pathname.split("/")[1]; 
  const params = useParams();
  const company = useAppSelector(selectCompany);
  const departments = useAppSelector(selectDepartments);
  const vehicles = useAppSelector(selectListAllVehiclesInDb);
  const listAllVehiclesInDbStatus = useAppSelector(selectListAllVehiclesInDbStatus);

  const department = useMemo(() => {
    if (departments) {
        return departments.find((department) => department.uuid === params.id);
    }
  }, [departments, params.id]);

  const vehiclesByDepartment = useMemo(() => {
    return vehicles.filter((vehicle) => vehicle.department === params.id);
  }, [params.id, vehicles]);

  useEffect(() => {
    if (!department) {
      navigate(`/${firstPath}/departments`);
    }
  }, [navigate, department, firstPath, vehiclesByDepartment]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/departments`);
  }, [firstPath, navigate]);

  if (!department) {
    return;
  }

  if (!vehiclesByDepartment) return <LaunchPage hasBorderRadius={true} />;

  return (
    <SidePanelDS.SidePanel
      visible={true}
      onClose={onSidePanelClosed}
      absoluteSection={
        <>
          {props.canDeactivate && (
            <>
              <ButtonDS
                leftIcon={<AddOutlined />}
                text={t("departments.sidepanel.addVehicle")}
                format={"fill"}
                buttonType={"primary"}
                onClick={() => console.log("association du véhicule")}
                color={"primary"}
              />
              <Spacer y={1} />
            </>
          )}
          {props.canAddVehicle && (
            <ButtonDS
              leftIcon={<NotInterestedOutlined />}
              text={t("departments.sidepanel.delete")}
              format={"fill"}
              buttonType={"secondary"}
              onClick={() => console.log("suppression du département")}
              iconColor={"errorNormal"}
            />
          )}
        </>
      }
    >
      <SidePanelDS.Section verticalSpacing={1.2} horizontalSpacing={1.5}>
        <StyledPanelContainer>
          <StyledContent>{department.name}</StyledContent>
        </StyledPanelContainer>
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>
            {t("departments.sidepanel.associatedVehicles")}
          </TextCapitalized>
        </SectionTitle>
        <Spacer y={2} />
        <StyledVehicleList>
            {vehiclesByDepartment.map((vehicle) => (
                <StyledVehicleItem key={vehicle.uuid}>
                    <SidePanelDS.SectionedActionStripe
                    left={
                        <StyledRowCenter>
                        <div style={{ width: "2rem" }}>
                            <img
                            src={Car}
                            style={{ width: "100%", filter: "grayscale(100%)" }}
                            alt="Car"
                            />
                        </div>
                        <Spacer x={1} />
                        {vehicle.reference && (
                            <>
                              <Tag
                                  textColor={colors["colors/text/darkGrey"]}
                                  text={vehicle.reference}
                                  backgroundColor={
                                  colors["colors/surfaces/background/background_level0"]
                                  }
                                  borderColor={colors["colors/text/veryLightGrey"]}
                              />
                              <Spacer x={1} />
                            </>
                        )}
                        <StyledVehicleResume>{vehicle.registrationNumber}</StyledVehicleResume>
                        </StyledRowCenter>
                    }
                    right={
                        <StyledLittleIcon>
                        <RemoveRedEyeOutlined
                            key="vehicle"
                            onClick={() =>
                            navigate(`/home/vehicles/${vehicle.uuid}`)
                            }
                        />
                        <Spacer x={1} />
                        </StyledLittleIcon>
                    }
                    />
                </StyledVehicleItem>
            ))}
        </StyledVehicleList>
      </SidePanelDS.Section>
    </SidePanelDS.SidePanel>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledContent = styled.div`
  ${typographies["Header/H4"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  justify-content: start;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  };
  white-space: nowrap;
`;
const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledVehicleResume = styled.span`
  ${typographies["Body/XS"]};
  color: ${colors["colors/text/darkGrey"]};
  text-align: center;
`;

const StyledVehicleList = styled.div`
  border: .025rem solid ${colors["colors/text/veryLightGrey"]};
  border-radius: 1rem;
  overflow: hidden;
`;

const StyledVehicleItem = styled.div`
  &:not(:first-child) {
    border-top: .025rem solid ${colors["colors/text/veryLightGrey"]};
  }
`;
